// Interprets the opaque `effects` ids on data-driven events (see eventEngine.ts).
// An effect can trigger a battle (only if the player is present at the event's
// location when it completes) and/or apply world flags describing what
// happened even if the player wasn't there to see it — news travels, and a
// location's state must reflect what happened to it (see
// GAME_CREATION_INSTRUCTIONS.md's "visible traces" rule).

import type { BattleId, GameState } from "../types"
import { getEventById } from "./content"
import { getRitualDeadlineDay } from "./loopEngine"

interface EventOutcome {
  battleId: BattleId | null
  flags: string[]
  /**
   * What the player should be told happened, when it happened somewhere they
   * were not standing. A world that silently rearranges itself off-screen is
   * unplayable in a game about learning its schedule — the player must hear
   * that Goldenfields burned on the day it burned, not discover it days later
   * by walking into the ashes.
   *
   * Null for outcomes the player witnesses directly (they are about to fight
   * the battle, so they hardly need telling).
   */
  report: string | null
}

function resolveDawnmereOutcome(state: GameState, playerPresent: boolean): EventOutcome {
  const evacuated = state.loop.worldFlags.includes("dawnmere_evacuated")
  const convinced = state.loop.worldFlags.includes("dawnmere_soldiers_convinced")

  if (evacuated) {
    // Everyone got out safely, but no one was left to defend the village itself.
    return {
      battleId: null,
      flags: ["dawnmere_burned"],
      report:
        "The orcs came to Dawnmere and found it empty. The people are alive; the village is ash.",
    }
  }
  if (!playerPresent) {
    // Not evacuated, not defended, and the player wasn't there to fight either.
    return convinced
      ? {
          battleId: null,
          flags: [],
          report:
            "Word from Dawnmere: the guard stood ready and turned the orcs back without you.",
        }
      : {
          battleId: null,
          flags: ["dawnmere_burned"],
          report:
            "Word from Dawnmere: the orcs came, nobody was expecting them, and the village burned.",
        }
  }
  return {
    battleId: convinced ? "dawnmere_orc_attack_defended_battle" : "dawnmere_orc_attack_battle",
    flags: [],
    report: null,
  }
}

/**
 * Mirrors resolveDawnmereOutcome. Distinct from Dawnmere in that a full
 * prevention path also exists (see "goldenfields_raid_prevented" in
 * events.json's preventedByFlags) for players who gathered evidence from
 * both Goldenfields and Rivergate — that path means this resolver never
 * runs at all, since the event never completes.
 */
function resolveGoldenfieldsOutcome(state: GameState, playerPresent: boolean): EventOutcome {
  const evacuated = state.loop.worldFlags.includes("goldenfields_evacuated")
  const militiaRallied = state.loop.worldFlags.includes("goldenfields_militia_rallied")

  if (evacuated) {
    return {
      battleId: null,
      flags: ["goldenfields_destroyed"],
      report:
        "Goldenfields was empty when the raiders reached it. The farmhands live; the harvest and the granary do not.",
    }
  }
  if (!playerPresent) {
    return militiaRallied
      ? {
          battleId: null,
          flags: [],
          report:
            "Word from Goldenfields: the militia you rallied held the raid off on their own.",
        }
      : {
          battleId: null,
          flags: ["goldenfields_destroyed"],
          report: "Word from Goldenfields: orcs raided the fields unopposed. Nothing is left standing.",
        }
  }
  return {
    battleId: militiaRallied
      ? "goldenfields_orc_raid_defended_battle"
      : "goldenfields_orc_raid_battle",
    flags: [],
    report: null,
  }
}

/** The siege's opening beat: no battle yet, just a visible "Under Attack" mark on the map. */
function resolveIronwatchSiegeBegins(): EventOutcome {
  return {
    battleId: null,
    flags: ["ironwatch_fortress_under_attack"],
    report: "Ironwatch Fortress is under siege. The orcs have not tried the walls yet.",
  }
}

/**
 * The siege's resolution, days after it begins. Ironwatch keeps a standing
 * garrison (unlike Dawnmere/Goldenfields, its battle always includes
 * defenders) so the only question is whether the player is there to help
 * fight it off, or whether the fortress falls to the orcs in their absence.
 */
function resolveIronwatchSiegeResolves(
  _state: GameState,
  playerPresent: boolean,
): EventOutcome {
  if (!playerPresent) {
    return {
      battleId: null,
      flags: ["ironwatch_fortress_controlled_by_enemy"],
      report:
        "Ironwatch has fallen. The garrison broke without relief, and orc banners fly over the walls.",
    }
  }
  return { battleId: "ironwatch_siege_battle", flags: [], report: null }
}

/**
 * The Phase 5 enemy incursions all share a shape: a deterministic attack the
 * player either stands and fights (producing a real stack battle whose outcome
 * their army composition decides) or is simply absent for — in which case
 * nobody stops it and the location carries the mark for the rest of the loop.
 */
function contestedAttack(
  battleId: BattleId,
  absentFlags: string[],
  absentReport: string,
): (state: GameState, playerPresent: boolean) => EventOutcome {
  return (_state, playerPresent) =>
    playerPresent
      ? { battleId, flags: [], report: null }
      : { battleId: null, flags: absentFlags, report: absentReport }
}

/**
 * A ritual milestone the player is never present for: the Necromancer's work
 * counting itself down toward Day 512, felt as the light failing across the
 * whole world. No battle and no flag — only news, so the 512-day clock the
 * whole campaign is built around is something the player watches tighten rather
 * than a number they have to keep in their head. (The atmosphere layer is
 * already dimming the UI toward night off the day counter; this puts words to
 * what the screen is doing.)
 */
function ritualOmen(report: string): () => EventOutcome {
  return () => ({ battleId: null, flags: [], report })
}

const EVENT_OUTCOME_RESOLVERS: Record<
  string,
  (state: GameState, playerPresent: boolean) => EventOutcome
> = {
  start_dawnmere_battle: resolveDawnmereOutcome,
  start_goldenfields_battle: resolveGoldenfieldsOutcome,
  begin_ironwatch_siege: resolveIronwatchSiegeBegins,
  resolve_ironwatch_siege: resolveIronwatchSiegeResolves,
  // Each absent-outcome carries both a narrative scar flag (read by the
  // location screen) and, where it applies, the conventional
  // "${locationId}_destroyed" / "_controlled_by_enemy" flag the world map
  // reads — so an unattended attack shows up in both places.
  start_ironwatch_dragon_raid: contestedAttack(
    "ironwatch_dragon_raid_battle",
    ["ironwatch_dragon_burned_fortress", "ironwatch_fortress_destroyed"],
    "A dragon came down on Ironwatch with no massed archery there to meet it. The yard is a black scar and the granaries are gone.",
  ),
  start_goldenfields_troll_incursion: contestedAttack(
    "goldenfields_troll_incursion_battle",
    ["goldenfields_overrun_by_trolls", "goldenfields_destroyed"],
    "Trolls came up out of the Mire into the Goldenfields. With no fire brought against them, nothing stopped them.",
  ),
  start_rivergate_night_raid: contestedAttack(
    "rivergate_night_raid_battle",
    ["rivergate_docks_lost"],
    "Dark Elves worked the Rivergate docks in the night. Nobody saw who did it, and the wharfs are shuttered.",
  ),
  start_temple_anchor_battle: contestedAttack(
    "temple_anchor_battle",
    ["temple_anchor_stands", "ruined_temple_first_dawn_controlled_by_enemy"],
    "Something beneath the temple floor woke and began holding the dead upright. It still stands, and it is still producing them.",
  ),
  start_crossroads_giant_warband: contestedAttack(
    "crossroads_giant_warband_battle",
    ["kings_road_closed", "kings_road_crossroads_controlled_by_enemy"],
    "Hill Giants came down onto the crossroads unopposed. The King's Road is closed behind them, and there is no way around.",
  ),
  start_blacktooth_rift_battle: contestedAttack(
    "blacktooth_rift_battle",
    ["blacktooth_rift_widened"],
    "A tear opened in the Blacktooth Wastes with nobody there to close it. It is feeding the orcs, and it is widening.",
  ),
  // The Necromancer's ritual, marking its own progress toward Day 512. These
  // fire wherever the player is; the light fails for everyone at once.
  ritual_darkening_first: ritualOmen(
    "You can see it for yourself if you look west at noon: the sun is dimmer than it was, and colder. The Necromancer's ritual has taken its first real bite out of the light. There is still a long way to the five hundred and twelfth day — but the clock is no longer an abstraction.",
  ),
  ritual_darkening_second: ritualOmen(
    "The days are visibly shorter now, and the noon sky keeps a bruised, brown cast that will not lift. Crops are failing early across the heartland. Whatever is being worked at the Ebon Citadel is past its halfway mark, and everyone can feel it, whether or not they have a name for it.",
  ),
  ritual_darkening_third: ritualOmen(
    "It is never fully light any more. The sun rises the colour of an old wound and gives no warmth, and people have stopped pretending not to be afraid. The ritual is nearly finished. If the Necromancer is not thrown down soon, there will be no soon.",
  ),
  // Day 512, with the Necromancer still alive. The loop does not merely
  // continue past here — it ends. App.tsx reads sun_extinguished and turns the
  // world over, which is the "one loop reaching the Necromancer too late" the
  // campaign is meant to allow.
  ritual_extinguishes_sun: () => ({
    battleId: null,
    flags: ["sun_extinguished"],
    report:
      "On the five hundred and twelfth day the sun goes out. Not sets — goes out, like a pinched wick, and the cold comes down all at once. Wherever you were standing, whatever you still had left to do, none of it matters now. The world holds its breath in the dark. And then, somewhere behind your eyes, a familiar morning begins to break.",
  }),
}

function getNewlyCompletedEventIds(prev: GameState, next: GameState): string[] {
  return next.loop.completedEvents.filter(
    (id) => !prev.loop.completedEvents.includes(id),
  )
}

function collectOutcomes(prev: GameState, next: GameState): EventOutcome[] {
  // Stationary actions (Rest, Training, Investigating Ruins) keep the player
  // at the same location for every day they span, so the player is present
  // there for the whole range — not only on the day the action happens to
  // finish. Travel is different: the destination only truly has the player
  // there once they arrive, so a multi-day journey through an event's day
  // at some other (or not-yet-reached) location must not count as presence.
  const stayedPut = prev.loop.currentLocationId === next.loop.currentLocationId

  const outcomes: EventOutcome[] = []
  for (const eventId of getNewlyCompletedEventIds(prev, next)) {
    const event = getEventById(eventId)
    if (!event) continue
    const playerPresent = stayedPut
      ? event.locationId === next.loop.currentLocationId
      : event.locationId === next.loop.currentLocationId && event.day === next.loop.currentDay
    for (const effect of event.effects) {
      const resolver = EVENT_OUTCOME_RESOLVERS[effect]
      if (resolver) outcomes.push(resolver(next, playerPresent))
    }
  }
  return outcomes
}

/**
 * Resolves any events that newly completed between prev and next into a
 * battle to fight (if the player was present), world flags to apply
 * (regardless of presence, so absent consequences are still visible later),
 * and reports to show the player for the ones they were not there for.
 */
export function resolveTriggeredEvents(
  prev: GameState,
  next: GameState,
): { battleId: BattleId | null; flags: string[]; reports: string[] } {
  const outcomes = collectOutcomes(prev, next)
  return {
    battleId: outcomes.find((o) => o.battleId)?.battleId ?? null,
    flags: [...outcomes.flatMap((o) => o.flags), ...deadlineFlags(prev, next)],
    reports: outcomes.flatMap((o) => (o.report ? [o.report] : [])),
  }
}

/**
 * The ritual gains a week on the world every loop it is left unfinished, so a
 * later loop's deadline can fall well before the fixed Day-512 backstop event
 * in events.json. This enforces *this* loop's (earlier) deadline: the day the
 * player's advance crosses it with the Necromancer still standing, the sun goes
 * out — the same "sun_extinguished" hand-off to App.tsx the Day-512 event makes,
 * so the loop turns over exactly as it would have at 512. Fires once, on the
 * crossing; App ends the loop before the day can be advanced again.
 */
function deadlineFlags(prev: GameState, next: GameState): string[] {
  if (!next.loop.alive) return []
  if (next.loop.worldFlags.includes("necromancer_defeated")) return []
  const deadline = getRitualDeadlineDay(next.loop.loopNumber)
  const crossedNow = prev.loop.currentDay < deadline && next.loop.currentDay >= deadline
  return crossedNow ? ["sun_extinguished"] : []
}
